// item 1098 (Tom's live-contract bug, via Chad) — MERGE-AWARE Statement-of-Work regeneration.
//
// THE PROBLEM: the SOW auto-builds from the venue model until you type in it; after that,
// auto-updates pause (item 994, so we never clobber your writing). But then a venue change —
// e.g. switching a venue from Medium to FLEX — never reaches the text, so the SOW says
// "Medium system" while the deal is flex. The only cure was "Regenerate", which rebuilt from
// scratch and DELETED every manual sentence. Tom lost his notes exactly that way.
//
// THE FIX: merge instead of overwrite. This module takes the freshly-composed scope and the
// user's current text and produces a document that has BOTH — regenerated structure, preserved
// writing. Pure + deterministic (no React), so it's unit-tested alongside rnwyScopeBuilder.
//
// The merge rules, in plain terms:
//   • Sections are matched by IDENTITY, not by their full heading — a venue section is keyed on
//     the venue NAME, so "## Nowhere Lounge — Medium system" and "## Nowhere Lounge — Flex …
//     system" are the same section and the heading simply refreshes to the new size.
//   • A generated bullet is matched by its ITEM LABEL (the text after "N × "), so the QUANTITY
//     refreshes from the venue model while anything the user appended to that line survives.
//   • Lines the user added that don't correspond to anything generated are kept, at the end of
//     the section they were written in.
//   • Whole sections the user wrote themselves are kept, after the generated ones.

interface Section { key: string; heading: string | null; lines: string[] }

const HEADING = /^#{1,6}\s+/;

// item 1112 (Chad: duplicated a quote, deleted venues from it, regenerated — the DELETED venues
// were still in the text). The original merge kept every section it couldn't match, on the rule
// "never silently delete someone's writing". But a REMOVED venue's section is indistinguishable
// from a hand-written one by that test, so scoping a quote DOWN could never take effect: the
// deleted venue rode along forever and the customer read scope they weren't buying.
//
// The fix is to recognise the shapes the GENERATOR owns. Those sections belong to the venue
// model, not to the writer, so when one stops being generated the venue is gone and the section
// goes with it. Anything else — "## Site conditions", "## Assumptions" — is still the user's and
// is still kept, unconditionally. The caller reports what was dropped, so it is never silent.
const GENERATED_SECTION = [
  /^system scope$/,
  /^high-availability$/,
  /^property-wide$/,
  /^switchpilot\b/,
  // a venue block, e.g. "Grand Ballroom — Medium system" / "Lobby — Flex add-on venue system"
  /\s—\s.*system$/,
];

const isGeneratedSection = (heading: string | null): boolean => {
  if (heading === null) return false;
  const text = heading.replace(HEADING, '').trim().toLowerCase();
  return GENERATED_SECTION.some((re) => re.test(text));
};

// a venue heading embeds the system size ("## Nowhere Lounge — Flex add-on venue … system"),
// which CHANGES when the size changes — so the identity is the part before the em-dash.
const sectionKey = (heading: string | null): string => {
  if (heading === null) return ' preamble';
  const text = heading.replace(HEADING, '').trim();
  const [head] = text.split(' — ');
  return head.trim().toLowerCase();
};

const splitSections = (doc: string): Section[] => {
  const out: Section[] = [];
  let cur: Section = { key: sectionKey(null), heading: null, lines: [] };
  doc.split('\n').forEach((line) => {
    if (HEADING.test(line)) {
      out.push(cur);
      cur = { key: sectionKey(line), heading: line, lines: [] };
    } else {
      cur.lines.push(line);
    }
  });
  out.push(cur);
  // drop a leading empty preamble
  return out.filter((s, i) => !(i === 0 && s.heading === null && s.lines.join('').trim() === ''));
};

const isBullet = (line: string): boolean => /^\s*-\s+/.test(line);

// item 1259 — the per-line "[NOTE: …]" annotation (item 1185) is GENERATOR-OWNED, like the
// quantity: it comes from the venue builder's note field, so the merge must refresh it from
// the generated line (and ignore it when matching identities). Before this, "Update" kept a
// stale note forever — and a CHANGED note broke the identity match, duplicating the line.
const NOTE_SUFFIX = /\s*—\s*\[NOTE:[^\]]*\]\s*$/;
const stripNote = (s: string): string => s.replace(NOTE_SUFFIX, '').trim();
const noteSuffixOf = (line: string): string | null => {
  const m = line.match(/\[NOTE:\s*([^\]]*)\]\s*$/);
  return m ? ` — [NOTE: ${m[1].trim()}]` : null;
};

// item 1400 (Chad: "scope quantity of x should always match the pricing") — a quantity is
// ENGINE DATA, not prose. Two generated shapes carry one: "N × label" (catalog lines) and the
// bare "N label" line (the SwitchPilot "69 managed Extreme switches" bullet). Both are keyed on
// the label and their number refreshes from the venue model; before this only the × form did,
// so a changed switch count left the old line standing beside the new one.
const QTY = /^(\d+(?:\.\d+)?)(\s*×\s*|\s+)(?=\S)/;

// the stable identity of a generated bullet: everything after the quantity prefix ("N × " or
// "N "), with any [NOTE: …] suffix stripped (falling back to the whole bullet body when there's
// no quantity).
const bulletBody = (line: string): string => {
  const body = line.replace(/^\s*-\s+/, '');
  const m = body.match(QTY);
  return stripNote((m ? body.slice(m[0].length) : body).trim());
};

const quantityPrefix = (line: string): string | null => {
  const body = line.replace(/^\s*-\s+/, '');
  const m = body.match(QTY);
  return m ? m[0] : null;
};

// item 1392 (Chad: "the text we hand-type is to be left alone on the same lines") — the merge
// only recognised a generated line when the user had APPENDED to it. Editing INSIDE a generated
// sentence (or a bullet's label) broke the identity: the generated original came back AND the
// user's version was appended at the end of the section — a duplicate, and the writing moved.
// Two lines are now the same line when they share a recognisable head: the first six words
// (case/punctuation-insensitive), or one is a prefix of the other. A line rewritten so heavily
// that none of the generated words remain is, honestly, the user's own line — nothing can tell.
const words = (s: string): string[] => s.toLowerCase()
  .replace(/[*_`~#>]/g, '')
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .split(/\s+/)
  .filter(Boolean);
const HEAD_WORDS = 6;
const sameHead = (a: string, b: string): boolean => {
  const wa = words(a);
  const wb = words(b);
  if (wa.length === 0 || wb.length === 0) return false;
  const n = Math.min(HEAD_WORDS, wa.length, wb.length);
  if (n < 3 && (wa.length >= 3 || wb.length >= 3)) return false; // too short to trust
  for (let i = 0; i < n; i += 1) if (wa[i] !== wb[i]) return false;
  return true;
};

// does the user's bullet describe the same catalog item as the generated one? The user appends
// to the end ("… Selection**(3 Volume Gain Cards…)**"), so the generated label is a prefix —
// or (item 1392) edits the label and the head words still match.
const sameItem = (generated: string, mine: string): boolean => {
  const g = bulletBody(generated);
  const m = bulletBody(mine);
  if (!g) return false;
  return m === g || m.startsWith(g) || sameHead(g, m);
};

// item 1392 — the same question for a generated PROSE line (the venue lead sentence, a
// breakout label, a paragraph): exact, prefix, or matching head words.
const sameProse = (generated: string, mine: string): boolean => {
  const g = generated.trim();
  const m = mine.trim();
  if (!g || !m) return false;
  return m === g || m.startsWith(g) || sameHead(g, m);
};

const mergeSection = (gen: Section, mine: Section): Section => {
  const usedMine = new Set<number>();
  const lines: string[] = [];
  // item 1392 — where each of the user's lines ended up in the merged output, so lines the user
  // INSERTED between generated ones can be put back right after the line they followed instead
  // of being swept to the end of the section.
  const placedAt = new Map<number, number>(); // index in mine.lines → index in lines

  gen.lines.forEach((gLine) => {
    if (!isBullet(gLine)) {
      if (gLine.trim() === '') { lines.push(gLine); return; }
      // item 1392 — an EDITED generated prose line stays the user's, in place; generator-owned
      // bold labels ("**Included:** …") still refresh because the user rarely edits those and
      // an exact match wins anyway.
      const pIdx = mine.lines.findIndex((mLine, i) => !usedMine.has(i) && !isBullet(mLine) && sameProse(gLine, mLine));
      if (pIdx === -1) { lines.push(gLine); return; }
      usedMine.add(pIdx);
      placedAt.set(pIdx, lines.length);
      lines.push(mine.lines[pIdx] === gLine ? gLine : mine.lines[pIdx]);
      return;
    }
    const idx = mine.lines.findIndex((mLine, i) => !usedMine.has(i) && isBullet(mLine) && sameItem(gLine, mLine));
    if (idx === -1) { lines.push(gLine); return; }
    usedMine.add(idx);
    placedAt.set(idx, lines.length);
    // keep the user's text for this item, but refresh the GENERATOR-OWNED parts from the
    // venue model: the quantity prefix and (item 1259) the [NOTE: …] suffix
    let myLine = mine.lines[idx];
    const gQty = quantityPrefix(gLine);
    const myQty = quantityPrefix(myLine);
    if (gQty && myQty) {
      // item 1400 — refresh the number in either shape ("N × " or bare "N ")
      myLine = myLine.replace(/^(\s*-\s+)\d+(?:\.\d+)?(\s*×\s*|\s+)(?=\S)/, `$1${gQty}`);
    }
    const gNote = noteSuffixOf(gLine);
    myLine = myLine.replace(NOTE_SUFFIX, '');
    if (gNote) myLine = myLine.replace(/\s*$/, '') + gNote;
    lines.push(myLine);
  });

  // anything the user wrote in this section that isn't a generated line: keep it — item 1392:
  // right after the matched line it followed in the user's text (so an inserted note stays
  // under its bullet); only a line with no matched predecessor falls to the end of the section.
  const extraIdx = mine.lines
    .map((line, i) => i)
    .filter((i) => !usedMine.has(i) && mine.lines[i].trim() !== '' && !gen.lines.includes(mine.lines[i]));
  const tail: string[] = [];
  // insert from the bottom up so earlier insertion points stay valid
  [...extraIdx].reverse().forEach((i) => {
    let anchor = -1;
    // walk back to the nearest line of the user's that was placed (skipping other extras)
    for (let j = i - 1; j >= 0 && anchor === -1; j -= 1) {
      if (placedAt.has(j)) anchor = placedAt.get(j) as number;
    }
    if (anchor === -1) { tail.unshift(mine.lines[i]); return; }
    lines.splice(anchor + 1, 0, mine.lines[i]);
    // shift every placement after the insertion point
    placedAt.forEach((pos, k) => { if (pos > anchor) placedAt.set(k, pos + 1); });
  });
  if (tail.length > 0) {
    if (lines.length > 0 && lines[lines.length - 1].trim() !== '') lines.push('');
    tail.forEach((l) => lines.push(l));
  }
  return { key: gen.key, heading: gen.heading, lines };
};

export interface MergeReport {
  text: string;
  /** headings of generator-owned sections that no longer apply (e.g. a deleted venue) */
  dropped: string[];
  /** item 1391 — venue sections re-paired after a RENAME ("Venue" → "Meeting Rooms"), old → new */
  renamed: string[];
}

// item 1391 (Chad: renamed a venue "Venue" → "Meeting Rooms", the SOW still said "Venue") — a
// venue section is keyed on the venue NAME, so a rename made the old section look DELETED and the
// new one look NEW: the user's text in it was dropped and the fresh heading arrived bare. A
// rename leaves the venue COUNT the same, so when the unmatched generated venue sections and the
// unmatched user venue sections line up one-for-one, pair them in order — the heading refreshes
// to the new name and the writing inside survives. Unequal counts mean a real add/remove and the
// 1112 drop rule stays in charge.
const VENUE_SECTION = /\s—\s.*system$/;
const isVenueSection = (heading: string | null): boolean => (
  heading !== null && VENUE_SECTION.test(heading.replace(HEADING, '').trim().toLowerCase())
);

/**
 * Rebuild the SOW from `generated` while preserving everything the user wrote in `current`,
 * and report any generator-owned sections that were dropped because they no longer apply.
 * Passing an empty/blank `current` simply returns `generated`.
 */
export const mergeScopeWithReport = (generated: string, current: string): MergeReport => {
  if (!current || current.trim() === '') return { text: generated, dropped: [], renamed: [] };
  if (!generated || generated.trim() === '') return { text: current, dropped: [], renamed: [] };

  const gen = splitSections(generated);
  const mine = splitSections(current);
  const mineByKey = new Map<string, Section>();
  mine.forEach((s) => { if (!mineByKey.has(s.key)) mineByKey.set(s.key, s); });

  const usedKeys = new Set<string>();
  const genKeys = new Set(gen.map((g) => g.key));
  // item 1391 — rename pairing: unmatched generated venue sections ↔ unmatched user venue
  // sections, in order, only when the counts agree
  const renamedPairs = new Map<string, Section>(); // generated key → the user's renamed section
  const renamed: string[] = [];
  const genOrphans = gen.filter((g) => isVenueSection(g.heading) && !mineByKey.has(g.key));
  const mineOrphans = mine.filter((s) => isVenueSection(s.heading) && !genKeys.has(s.key));
  if (genOrphans.length > 0 && genOrphans.length === mineOrphans.length) {
    genOrphans.forEach((g, i) => {
      renamedPairs.set(g.key, mineOrphans[i]);
      renamed.push(`${(mineOrphans[i].heading as string).replace(HEADING, '').split(' — ')[0].trim()} → ${(g.heading as string).replace(HEADING, '').split(' — ')[0].trim()}`);
    });
  }

  const merged = gen.map((g) => {
    const m = mineByKey.get(g.key) ?? renamedPairs.get(g.key);
    if (!m) return g;
    usedKeys.add(m.key);
    return mergeSection(g, m);
  });

  // unmatched sections split two ways: the user's own writing rides at the end, while a
  // generator-owned section that is no longer generated has been REMOVED from the quote (item
  // 1112) and must go with it.
  const unmatched = mine.filter((s) => !usedKeys.has(s.key) && s.heading !== null);
  const userOnly = unmatched.filter((s) => !isGeneratedSection(s.heading));
  const dropped = unmatched
    .filter((s) => isGeneratedSection(s.heading))
    .map((s) => (s.heading as string).replace(HEADING, '').trim());

  const render = (s: Section): string => [s.heading, ...s.lines].filter((l) => l !== null).join('\n');
  const text = [...merged, ...userOnly]
    .map(render)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, dropped, renamed };
};

/** Back-compatible text-only merge. */
export const mergeScope = (generated: string, current: string): string => (
  mergeScopeWithReport(generated, current).text
);

export default mergeScope;
